import { Fragment } from "react";
import { Navigate, Outlet, Route, isRouteErrorResponse, useRouteError } from "react-router-dom";
import { PackagingList } from "./PackagingsList";
import { CreatePackagingPage } from "./PackagingForm/CreatePackaging";
import { UpdatePackaging } from "./PackagingForm/UpdatePackaging";
import { useTranslation } from "react-i18next";
import { QuestionnaireUpdate } from "./PackagingDetails/Questionnaire/QuestionnaireUpdate/QuestionnaireUpdate";
import { PackagingContextProvider } from "./PackagingDetails/PackagingContext";
import { PackagingPrimaryTabs } from "./PackagingDetails/PackagingPrimaryTabs";
import { Forbiddenpage } from "src/components/PageState/ForbiddenPage";
import { CREATE, PACKAGING, MODIFY, useFoodpilotContext, VIEW } from "src/context/FoodpilotContext";
import { Slugs } from "src/context/ProtectedContext";
import { PackagingScores } from "./PackagingDetails/PackagingScores";
import { Questionnaire } from "./PackagingDetails/Questionnaire/Questionnaire";
import { Informations } from "./PackagingDetails/Informations";
import PackagingScoreContextProvider from "src/pages/Packaging/PackagingDetails/Scores/PackagingScoreContextProvider";
import { ScoreDetail } from "src/components/ScoreDetail/ScoreDetail";
import { DimensionTreeSwitch } from "src/components/ScoreDetail/Dimensions/DimensionTreeSwitch";

export const usePackagingRouter = () => {
    return (
        <Fragment>
            <Route path="packagings" errorElement={<PackagingError />}>
                <Route element={<PackagingActionProtection slug={VIEW} />}>
                    <Route index element={<PackagingList />} />
                </Route>

                <Route path={"new"} element={<PackagingActionProtection slug={CREATE} />}>
                    <Route element={<CreatePackagingPage />}>
                        <Route index />
                        <Route path={":startingPageId"} />
                    </Route>
                </Route>

                <Route path=":packagingId" element={<ExistingPackaging />}>
                    <Route path={"edit"} element={<PackagingActionProtection slug={MODIFY} />}>
                        <Route element={<UpdatePackaging />}>
                            <Route index />
                            <Route path={":startingPageId"} />
                        </Route>
                    </Route>

                    <Route element={<PackagingActionProtection slug={VIEW} />}>
                        <Route index element={<Navigate to="scores" replace />} />

                        <Route path="scores" element={<PackagingPrimaryTabs selectedSection="scores" />}>
                            <Route index element={<PackagingScores />} />

                            <Route path=":scoreId" element={<ExistingPackagingScore />}>
                                <Route index element={<ScoreDetail selectedTab={null} />} />
                                <Route path="distribution" element={<ScoreDetail selectedTab={"distribution"} />}>
                                    <Route index element={<DimensionTreeSwitch mode={"distribution"} />} />
                                </Route>
                            </Route>
                        </Route>

                        <Route
                            path={"questionnaire"}
                            element={<PackagingPrimaryTabs selectedSection={"questionnaire"} />}
                        >
                            <Route index element={<Questionnaire />} />
                        </Route>
                        <Route path="questionnaire/edit/:headingId" element={<QuestionnaireUpdate />} />

                        <Route path={"informations"}>
                            <Route element={<PackagingPrimaryTabs selectedSection={"informations"} />}>
                                <Route index element={<Informations />} />
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Fragment>
    );
};

type PackagingActionProtectionProps = {
    slug: Slugs;
};
const PackagingActionProtection = (props: PackagingActionProtectionProps) => {
    const { slug } = props;
    const { canUser } = useFoodpilotContext();

    if (canUser(slug, PACKAGING) === false) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};

const ExistingPackaging = () => {
    return (
        <PackagingContextProvider>
            <Outlet />
        </PackagingContextProvider>
    );
};

const ExistingPackagingScore = () => {
    return (
        <PackagingScoreContextProvider>
            <Outlet />
        </PackagingScoreContextProvider>
    );
};

const PackagingError = () => {
    const error = useRouteError();
    const { t } = useTranslation();

    if (isRouteErrorResponse(error)) {
        if (error.status === 404) {
            return <div>{t("Page inexistante")} 404</div>;
        } else if (error.status === 401) {
            return <div>{t("Unauthorized")}: 401</div>;
        } else if (error.status === 503) {
            return <div>API down: 503</div>;
        } else if (error.status === 500) {
            return <div>API error: 500</div>;
        }
    }
    console.error(error);
    return <div>Hello this is an Error from Packaging</div>;
};
